import { ColumnDef } from "@tanstack/react-table";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { formatDate } from "../../../helpers/appHelpers";

// ─── Portal tooltip for employee names ────────────────────────────────────────
// Rendered via a portal so it escapes the table row's overflow:hidden clipping.

interface EmployeeTooltipProps {
	names: string[];
	anchorRef: React.RefObject<HTMLSpanElement>;
}

const EmployeeTooltip = ({ names, anchorRef }: EmployeeTooltipProps) => {
	const [pos, setPos] = useState({ top: 0, left: 0 });

	useEffect(() => {
		if (anchorRef.current) {
			const rect = anchorRef.current.getBoundingClientRect();
			setPos({
				top: rect.bottom + window.scrollY + 6,
				left: rect.left + window.scrollX + rect.width / 2,
			});
		}
	}, [anchorRef]);

	return createPortal(
		<div
			style={{
				position: "absolute",
				top: pos.top,
				left: pos.left,
				transform: "translateX(-50%)",
				zIndex: 9999,
				backgroundColor: "#fff",
				border: "1px solid #e5e7eb",
				borderRadius: "8px",
				boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
				padding: "6px 12px",
				minWidth: "max-content",
			}}
		>
			{names.map((name, i) => (
				<div
					key={i}
					style={{ fontSize: "12px", color: "#374151", padding: "2px 0" }}
				>
					{name}
				</div>
			))}
		</div>,
		document.body,
	);
};

interface EmployeeBadgeProps {
	count: number;
	names: string[];
}

const EmployeeMoreBadge = ({ count, names }: EmployeeBadgeProps) => {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLSpanElement>(null);

	return (
		<span
			ref={ref}
			onMouseEnter={() => setOpen(true)}
			onMouseLeave={() => setOpen(false)}
			className="bg-gray-100 text-gray-500 text-[11px] rounded-full px-2 py-0.5 cursor-default"
		>
			+{count} more
			{open && <EmployeeTooltip names={names} anchorRef={ref} />}
		</span>
	);
};

// ─── Custom Tax Relief Group ───────────────────────────────────────────────

interface CustomTaxReliefGroupRow {
	id: number;
	name: string;
	code: string;
	description?: string;
	active: boolean;
	createdAt?: string;
	modifiedAt?: string;
}

export const customTaxReliefGroupColumns: ColumnDef<CustomTaxReliefGroupRow>[] =
	[
		{
			id: "name",
			accessorKey: "name",
			header: "Name",
			size: 220,
		},
		{
			id: "code",
			accessorKey: "code",
			header: "Code",
			size: 180,
		},
		{
			id: "description",
			accessorKey: "description",
			header: "Description",
			size: 400,
			cell: ({ row }) => row.original.description || "--",
		},
		{
			id: "active",
			accessorKey: "active",
			header: "Active",
			size: 140,
			cell: ({ row }) => (row.original.active ? "Yes" : "No"),
		},
		{
			id: "createdAt",
			accessorKey: "createdAt",
			header: "Date Created",
			size: 180,
			cell: ({ row }) =>
				row.original.createdAt ? formatDate(row.original.createdAt) : "--",
		},
		{
			id: "modifiedAt",
			accessorKey: "modifiedAt",
			header: "Date Modified",
			size: 180,
			cell: ({ row }) =>
				row.original.modifiedAt ? formatDate(row.original.modifiedAt) : "--",
		},
	];

export const customTaxReliefGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	active: true,
	createdAt: true,
	modifiedAt: false,
};

// ─── Employee Custom Tax Relief ────────────────────────────────────────────

interface CustomTaxReliefRow {
	id: number;
	name?: string;
	code?: string;
	amount: number;
	active: boolean;
	employeeCustomTaxReliefs?: {
		employee: { firstName: string; lastName: string };
	}[];
	employeeBandCustomTaxReliefs?: {
		employeeBand: { name: string };
	}[];
	group?: { name: string };
	startDate?: string;
	endDate?: string;
	createdAt?: string;
	modifiedAt?: string;
}

const MAX_VISIBLE_EMPLOYEES = 2;

export const customTaxReliefColumns: ColumnDef<CustomTaxReliefRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
		cell: ({ row }) => row.original.name || "--",
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
		cell: ({ row }) => row.original.code || "--",
	},
	{
		id: "group",
		accessorKey: "group.name",
		header: "Tax Relief Group",
		size: 220,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "employeeBand",
		accessorKey: "employeeBandCustomTaxReliefs",
		header: "Employee Band",
		size: 200,
		cell: ({ row }) => {
			const bands = row.original.employeeBandCustomTaxReliefs ?? [];
			if (!bands.length) return "--";
			return bands.map((r) => r.employeeBand.name).join(", ");
		},
	},
	{
		id: "employee",
		accessorKey: "employeeCustomTaxReliefs",
		header: "Employee",
		size: 280,
		cell: ({ row }) => {
			const reliefs = row.original.employeeCustomTaxReliefs ?? [];
			if (!reliefs.length) return "--";

			const names = reliefs.map(
				(r) => `${r.employee.firstName} ${r.employee.lastName}`,
			);
			const visible = names.slice(0, MAX_VISIBLE_EMPLOYEES);
			const hidden = names.slice(MAX_VISIBLE_EMPLOYEES);

			return (
				<div className="flex items-center gap-1.5 flex-wrap">
					{visible.map((name, i) => (
						<span
							key={i}
							className="bg-gray-100 text-gray-700 text-[11px] rounded-full px-2 py-0.5 whitespace-nowrap"
						>
							{name}
						</span>
					))}
					{hidden.length > 0 && (
						<EmployeeMoreBadge count={hidden.length} names={hidden} />
					)}
				</div>
			);
		},
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 160,
		cell: ({ row }) => Number(row.original.amount).toFixed(2),
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
	{
		id: "createdAt",
		accessorKey: "createdAt",
		header: "Date Created",
		size: 180,
		cell: ({ row }) =>
			row.original.createdAt ? formatDate(row.original.createdAt) : "--",
	},
	{
		id: "modifiedAt",
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 180,
		cell: ({ row }) =>
			row.original.modifiedAt ? formatDate(row.original.modifiedAt) : "--",
	},
];

export const customTaxReliefColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	group: true,
	employeeBand: true,
	employee: true,
	amount: true,
	active: true,
	createdAt: true,
	modifiedAt: false,
};
